import { Button, type CompanyFormat, formatMoney, TextButton, zonedInstant } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { TimePicker } from "@majitap/ui/arc/time-picker/time-picker";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useProductItems } from "../../catalog";
import { useCompanyFormat } from "../../company";
import { placesQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { CATEGORY, pricingActions, type Quote } from "../../pricing";
import { type Payer, PayerPicker } from "../invoices/payer";
import styles from "./pricing.module.css";

let nextKey = 0;

/**
 * The quote tool: what a customer or business would pay for items at one of their places, now,
 * with an optional voucher. It shows the API's breakdown exactly, costs and margin included.
 */
export function QuoteView(): ReactNode {
  const format = useCompanyFormat();
  const items = useProductItems();
  const [payer, setPayer] = useState<Payer | null>(null);
  const [placeId, setPlaceId] = useState("");
  const [lines, setLines] = useState([{ key: nextKey++, item: "", quantity: "1" }]);
  const [voucher, setVoucher] = useState("");
  // price as at a past moment: what an order or invoice should have cost, for a dispute
  const [asAt, setAsAt] = useState<Date | null>(null);
  const [asAtTime, setAsAtTime] = useState("12:00");
  const places = useQuery({ ...placesQuery(payer?.billee ?? { customer_id: "" }), enabled: Boolean(payer) });
  const quote = useMutation({
    mutationFn: () =>
      pricingActions.quote({
        ...(payer?.billee ?? {}),
        location_id: placeId,
        items: lines.map((l) => ({ product_item_id: l.item, quantity: l.quantity.trim() })),
        voucher_code: voucher.trim() || undefined,
        at: asAt ? zonedInstant(asAt, asAtTime, format.timezone) : undefined,
      }),
  });
  const ready = payer && placeId && lines.every((l) => l.item && /^\d{1,9}(\.\d{1,3})?$/.test(l.quantity.trim()));
  return (
    <div className={styles.panel}>
      <div className={styles.quoteForm}>
        <PayerPicker
          value={payer}
          onChange={(p) => {
            setPayer(p);
            setPlaceId("");
          }}
        />
        {payer ? (
          <Select
            label="Place"
            placeholder={places.data?.length === 0 ? "They have no places" : "Choose a place"}
            value={placeId}
            onValueChange={setPlaceId}
            options={(places.data ?? []).map((l) => ({ value: l.id, label: l.label }))}
          />
        ) : null}
        {lines.map((l, n) => (
          <div key={l.key} className={styles.line}>
            <Select
              label={`Item ${n + 1}`}
              placeholder="Choose an item"
              value={l.item}
              onValueChange={(v) => setLines(lines.map((x) => (x.key === l.key ? { ...x, item: v } : x)))}
              options={items.items.filter((i) => i.orderable).map((i) => ({ value: i.id, label: i.label }))}
            />
            <Input
              label="Quantity"
              inputMode="decimal"
              value={l.quantity}
              onChange={(e) => setLines(lines.map((x) => (x.key === l.key ? { ...x, quantity: e.target.value } : x)))}
            />
            {lines.length > 1 ? (
              <TextButton onClick={() => setLines(lines.filter((x) => x.key !== l.key))}>Remove</TextButton>
            ) : null}
          </div>
        ))}
        <div>
          <TextButton onClick={() => setLines([...lines, { key: nextKey++, item: "", quantity: "1" }])}>
            Add an item
          </TextButton>
        </div>
        <Input label="Voucher code (optional)" value={voucher} onChange={(e) => setVoucher(e.target.value)} />
        <SegmentedControl
          label="Prices as at"
          value={asAt ? "then" : "now"}
          onValueChange={(v) => setAsAt(v === "now" ? null : new Date(new Date().setHours(0, 0, 0, 0)))}
          options={[
            { value: "now", label: "Now" },
            { value: "then", label: "Another moment" },
          ]}
        />
        {asAt ? (
          <div className={styles.line}>
            <DatePicker label="Day" value={asAt} onChange={(d) => setAsAt(d ?? null)} />
            <TimePicker label="At" value={asAtTime} onChange={setAsAtTime} format="24h" minuteStep={30} />
          </div>
        ) : null}
        <div className={styles.actions}>
          <Button disabled={!ready} loading={quote.isPending} onClick={() => quote.mutate()}>
            Get a quote
          </Button>
        </div>
      </div>
      {quote.error ? (
        <Alert tone="danger" title="No quote">
          {messageFor(errorOf(quote.error))}
        </Alert>
      ) : null}
      {quote.data ? <QuoteBreakdown quote={quote.data} format={format} labelOf={items.labelOf} /> : null}
    </div>
  );
}

/**
 * Every line and total the API returned, as it returned them: the amounts are its decimal
 * strings, formatted, never added up here.
 */
export function QuoteBreakdown({
  quote,
  format,
  labelOf,
}: {
  quote: Quote;
  format: Pick<CompanyFormat, "currency" | "locale">;
  labelOf: (id: string | null) => string;
}) {
  const money = (v: string) => formatMoney(v, { ...format, currency: quote.currency });
  return (
    <div className={styles.panel}>
      {quote.deliveries.map((d, n) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: deliveries have no id; a quote is fixed, so its order is its identity
        <section key={n} className={styles.section}>
          <h3 className={styles.sectionTitle}>
            Delivery {n + 1}: {d.items.map((i) => `${i.quantity} × ${labelOf(i.product_item_id)}`).join(", ")}
          </h3>
          <SortableDataTable
            caption={`Delivery ${n + 1}`}
            rowKey={(l) => `${l.pricing_id ?? l.voucher_id}-${l.product_item_id}-${l.charge_type.id}`}
            rows={d.lines}
            itemName={{ one: "line", other: "lines" }}
            columns={[
              { key: "charge_type", label: "Charge", sortable: false, render: (_v, l) => l.charge_type.name },
              {
                key: "category",
                label: "Kind",
                sortable: false,
                render: (_v, l) => `${CATEGORY[l.category]}${l.direction === "payable" ? " (cost)" : ""}`,
              },
              {
                key: "quantity",
                label: "Qty",
                numeric: true,
                sortable: false,
                render: (_v, l) => (l.percent ? `${l.percent}% of ${money(l.base ?? "0")}` : l.quantity),
              },
              {
                key: "unit_amount",
                label: "Unit",
                numeric: true,
                sortable: false,
                render: (_v, l) => money(l.unit_amount),
              },
              { key: "amount", label: "Amount", numeric: true, sortable: false, render: (_v, l) => money(l.amount) },
            ]}
          />
          <p className={styles.muted}>
            Charged {money(d.receivable_total)} · costs {money(d.payable_total)} · margin {money(d.margin)}
          </p>
        </section>
      ))}
      {quote.voucher ? (
        <p className={styles.muted}>
          Voucher {quote.voucher.code}: {money(quote.voucher.discount_amount)} off.
        </p>
      ) : null}
      <div className={styles.totals} data-testid="quote-totals">
        <span className={styles.totalRow}>
          <strong>The customer pays</strong> <strong className={styles.amount}>{money(quote.receivable_total)}</strong>
        </span>
        <span className={styles.totalRow}>
          Costs <span className={styles.amount}>{money(quote.payable_total)}</span>
        </span>
        <span className={styles.totalRow}>
          Margin <span className={styles.amount}>{money(quote.margin)}</span>
        </span>
      </div>
    </div>
  );
}
